import RestControl from "./RestControl";
import SetRow from "./SetRow";
import TempoControl from "./TempoControl";
import BlockRevealRegion from "./BlockRevealRegion";
import BlockDragHandle, {
  BlockBodyAction,
  BlockBodyActions,
  BlockCardHeader,
  BlockThumbnail,
  DuplicatePath,
  TrashPath,
} from "./blockChrome";
import {
  isPlannedExerciseNoteTooLong,
  PLANNED_EXERCISE_NOTE_MAX_LENGTH,
  PLANNED_EXERCISE_NOTE_TOO_LONG_COPY,
} from "./plannedExerciseNote";

// Tier-3 builder presentation seam (Phase 7A) -- Design V2 single block.
//
// Presentation only. Exercise assignment, tempo drafts, rest cycling, the D36
// rep prescription, RIR cycling, add/remove set, notes, delete,
// convert-to-superset and the dnd-kit sortable bindings all stay owned by the
// Workout Builder container and arrive here as values and callbacks.
export default function BlockEditorSingle({
  block,
  blockUiKey,
  isCollapsed,
  isDragging,
  disabled = false,
  canAddSet,
  canRemoveSet,
  maxSetCount,
  tempoValue,
  repDraftsForSet,
  prescriptionForSet,
  sortable,
  onToggle,
  onDelete,
  onDuplicate,
  onConvertToSuperset,
  onTempoFocus,
  onTempoChange,
  onTempoBlur,
  onRestStep,
  onAddSet,
  onRemoveSet,
  onCycleRir,
  onRepFieldFocus,
  onRepFieldChange,
  onRepFieldBlur,
  onNotesChange,
}) {
  const tempoId = `lz-tempo-${blockUiKey}`;
  const setCount = block.sets.length;
  const measurementType = block.executionProfile?.measurementType || "REPS";
  const isTimed = measurementType === "TIMED_HOLD";

  return (
    <section
      ref={sortable.setNodeRef}
      style={sortable.style}
      data-block-id={blockUiKey}
      data-dragging={isDragging ? "true" : undefined}
      className="lz-builder-block lz-builder-block--single"
    >
      <BlockCardHeader
        isCollapsed={isCollapsed}
        toggleLabel={isCollapsed ? "Expand block" : "Collapse block"}
        onToggle={onToggle}
        dragHandle={
          <BlockDragHandle
            disabled={disabled}
            setHandleRef={sortable.setHandleRef}
            handleAttributes={sortable.handleAttributes}
            handleListeners={sortable.handleListeners}
          />
        }
        thumbnail={<BlockThumbnail exerciseId={block.exerciseId} />}
        title={block.exercise}
        meta={
          <p className="lz-builder-block__meta">
            {setCount} {setCount === 1 ? "set" : "sets"}
          </p>
        }
      />

      <BlockRevealRegion open={!isCollapsed}>
        {() => (
        <div className="lz-builder-block__body">
          <div className="lz-builder-block__controls">
            {isTimed ? null : <div className="lz-builder-field-group">
              <label className="lz-builder-field-label" htmlFor={tempoId}>
                Tempo
              </label>
              <TempoControl
                id={tempoId}
                value={tempoValue}
                disabled={disabled}
                onFocus={onTempoFocus}
                onChange={onTempoChange}
                onBlur={onTempoBlur}
              />
            </div>}

            <div className="lz-builder-field-group">
              <span className="lz-builder-field-label">Rest</span>
              <RestControl
                value={block.rest}
                disabled={disabled}
                onStep={onRestStep}
              />
            </div>
          </div>

          <div className="lz-builder-set-table">
            <div className="lz-builder-set-table__head" aria-hidden="true">
              <span>Set</span>
              <span>{isTimed ? "Time" : "Reps"}</span>
              <span>{isTimed ? "" : "RIR"}</span>
              <span />
            </div>

            <div className="lz-builder-set-table__rows">
              {block.sets.map((set, setIndex) => (
                <SetRow
                  key={`${block.id}-set-${setIndex}`}
                  setNumber={setIndex + 1}
                  label={`Set ${setIndex + 1}`}
                  prescription={prescriptionForSet(set, measurementType)}
                  repDrafts={repDraftsForSet(setIndex)}
                  rirValue={set.rpe}
                  measurementType={measurementType}
                  disabled={disabled}
                  canRemove={canRemoveSet}
                  onRemove={() => onRemoveSet(setIndex)}
                  onCycleRir={() => onCycleRir(setIndex, set.rpe)}
                  onRepFieldFocus={(field) => onRepFieldFocus(setIndex, set, field)}
                  onRepFieldChange={(field, value) => onRepFieldChange(setIndex, field, value)}
                  onRepFieldBlur={(field) => onRepFieldBlur(setIndex, set, field)}
                />
              ))}
            </div>
          </div>

          {canAddSet ? (
            <button
              type="button"
              onClick={onAddSet}
              disabled={disabled}
              className="lz-builder-block__add-set"
            >
              <span aria-hidden="true">+</span>
              <span>Add Set</span>
            </button>
          ) : (
            <p className="lz-builder-block__hint">
              Maximum of {maxSetCount} sets reached.
            </p>
          )}

          <textarea
            disabled={disabled}
            value={block.notes || ""}
            maxLength={PLANNED_EXERCISE_NOTE_MAX_LENGTH}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Add technical notes (e.g., focus on depth, slow eccentric)..."
            aria-label="Block notes"
            className="lz-builder-notes"
          />
          {isPlannedExerciseNoteTooLong(block.notes) ? (
            <p className="lz-builder-block__hint" role="status">{PLANNED_EXERCISE_NOTE_TOO_LONG_COPY}</p>
          ) : null}

          {/* Same callbacks as before -- only their location changed: the
              collapsed card no longer carries them. */}
          <BlockBodyActions
            trailing={
              <BlockBodyAction
                label="Delete Exercise"
                tone="danger"
                iconOnly
                disabled={disabled}
                onClick={onDelete}
              >
                <TrashPath />
              </BlockBodyAction>
            }
          >
            <BlockBodyAction
              label="Create Superset"
              shortLabel="Superset"
              disabled={disabled}
              onClick={onConvertToSuperset}
            >
              <path d="M3 6h10M3 10h10M6 2v12" />
            </BlockBodyAction>
            <BlockBodyAction
              label="Duplicate Block"
              shortLabel="Duplicate"
              disabled={disabled}
              onClick={onDuplicate}
            >
              <DuplicatePath />
            </BlockBodyAction>
          </BlockBodyActions>
        </div>
        )}
      </BlockRevealRegion>
    </section>
  );
}
